(function (angular) {
    'use strict';

    /**
     * Service to handle the final portion of the Fast Track process, uploading
     * receipts for products that have rebates. This service is responsible for:
     *  1. Collecting and storing the products that have rebates
     *  2. Handling/modifying the receipts on the rebated products (including linking
     *      receipts)
     *  3. Submitting the products for claim creation.
     */
    angular
        .module('enervee.fastTrack')
        .factory('FastTrackUploadReceiptsService', FastTrackUploadReceiptsService);

    FastTrackUploadReceiptsService.$inject = [
        'CategoriesService',
        'EnerveeRebateService',
        'RebatesService'
    ];

    function FastTrackUploadReceiptsService(CategoriesService, EnerveeRebateService, RebatesService) {
        var _pub = {
            clearReceiptData: clearReceiptData,
            duplicateReceiptData: duplicateReceiptData,
            getRebatedProducts: getRebatedProducts,
            setRebatedProducts: setRebatedProducts,
            getReceiptList: getReceiptList,
            submitClaim: submitClaim,
        };
        var _rebatedProducts = [];

        return _pub;


        /**
         * Format and add the given products/rebates to the service.
         *
         * @param {Object[]} products
         * @param {Object[]} rebates
         */
        function setRebatedProducts(products, rebates) {
            _rebatedProducts.splice(0);
            for (var productId in products) {
                _rebatedProducts.push(
                    _buildRebatedProduct(products[productId], rebates[productId])
                );
            }
            _rebatedProducts = _rebatedProducts.sort(_rebatedProductCompareFunction);

            _rebatedProducts.forEach(function (product) {
                RebatesService.getRebateTagText(product).then(function (text) {
                    product.rebateTagText = text;
                });
            });
        }

        /**
         * Retrieve the array of rebated products.
         *
         * @return {Array}
         */
        function getRebatedProducts() {
            return _rebatedProducts;
        }

        /**
         * Attempts to submit the claim form.
         * Any errors picked up on the backend will be displayed appropriately.
         *
         * @return {Promise}
         */
        function submitClaim() {
            var productsWithRebates = _rebatedProducts.filter(function(product) { return product.has_rebate; });
            return EnerveeRebateService.submitFastTrack(productsWithRebates);
        }

        /**
         * Copy the receipt data (using prototypal inheritance) from the originalProduct
         * to the given product.
         *
         * @param {RebatedProduct} product
         * @param {number} originalProductID
         */
        function duplicateReceiptData(product, originalProductID) {
            if (!originalProductID) {
                clearReceiptData(product);
                return;
            }

            // Using .filter because .find isn't supported in IE
            var originalProduct = _rebatedProducts.filter(function(product) {
                return product.product_id === originalProductID;
            })[0];

            // Set the prototype of the receipt to the receipt object we want to link it to.
            // This allows the receipt to fallback to any values in it's prototype.
            _setReceiptPrototype(product.receipt, originalProduct.receipt);
            product.is_locked = true;
        }

        /**
         * Reset and clear the receipt data of the given product.
         *
         * @param {RebatedProduct} product
         */
        function clearReceiptData(product) {
            Object.setPrototypeOf(product.receipt, {});
            product.is_locked = false;
        }

        /**
         * Retrieve a list of products that are valid options for linking
         * receipts.
         *
         * @param {RebatedProduct} product
         * @return {{product_name: string, product_id: int}[]}
         */
        function getReceiptList(product) {
            return _rebatedProducts
                .filter(function(prod) { return prod.has_rebate; })
                .slice(0, _rebatedProducts.indexOf(product))
                .map(function(prod) {
                    return {
                        product_name: prod.product_name,
                        product_id: prod.product_id,
                    };
                });
        }

        /**
         * Given a product and it's rebate, build a RebatedProduct object.
         *
         * @param {Object} product
         * @param {Object} rebate
         * @return {RebatedProduct}
         * @private
         */
        function _buildRebatedProduct(product, rebate) {
            const category = CategoriesService.getCategoryById(rebate.category_id);

            return {
                product_name: product.display_name,
                category: category,
                rebate: rebate.rebate,
                rebates: rebate.rebates,
                image: product.image_url,
                product_id: product.id,
                rebate_quantity: rebate.rebate_quantity,
                rebate_id: rebate.rebate_id,
                rebate_amount: rebate.rebate_amount,
                has_rebate: rebate.rebate_quantity > 0,
                reason: rebate.reason,
                receipt: {},
                is_locked: false,
            };
        }

        /**
         * Comparison function for rebated products. For this function, products are placed
         * into two tiers, has rebates and does not have rebates. Products without rebates
         * are placed at the end of the array, while preserving the order of equivalent products.
         *
         * @param {RebatedProduct} prod1
         * @param {RebatedProduct} prod2
         * @return {number}
         * @private
         */
        function _rebatedProductCompareFunction(prod1, prod2) {
            if (!prod1.has_rebate && !prod2.has_rebate) {
                return 0;
            } else if (!prod1.has_rebate) {
                return 1;
            } else if (!prod2.has_rebate) {
                return -1;
            }
            return 0;
        }

        /**
         * Handles manipulating the receipt object to correctly set the
         * prototype and remove unnecessary data that could shadow the prototype.
         *
         * @param {Receipt} receipt
         * @param {Receipt} parentReceipt
         * @private
         */
        function _setReceiptPrototype(receipt, parentReceipt) {
            Object.setPrototypeOf(receipt, parentReceipt);
            // The purchase price must always be set to something, so that we don't
            // see the parent's purchase price.
            receipt.purchasePrice = receipt.hasOwnProperty('purchasePrice') ?
                receipt.purchasePrice :
                undefined;
            // Delete the remaining variables that could shadow the parent's.
            delete receipt.purchaseDate;
            delete receipt.storeName;
            delete receipt.receiptID;
        }
    }

    /**
     * A product that has rebate information attached to it, which is in
     * the process of having the rebate claimed.
     * @typedef {Object} RebatedProduct
     * @property {string} product_name - The display name of the product.
     * @property {string} image - The URL for an image of the product.
     * @property {number} product_id - The internal ID of the product.
     * @property {number} rebate_quantity - The quantity of rebates that can be claimed.
     * @property {number|null} rebate_id - The ID of the rebate (or null if there is no rebate).
     * @property {number|null} rebate_amount - The unit amount of the rebate (or null if there is no rebate).
     * @property {boolean} has_rebate - Indicates whether the product has a rebate.
     * @property {Receipt} receipt - The receipt that will be submitted for the product.
     * @property {boolean} is_locked - Indicates whether the product receipt should be considered locked.
     */

    /**
     * A receipt for a given product.
     * @typedef {Object} Receipt
     * @property {file[]} images - An array of images for the receipt.
     * @property {string} storeName - The name of the store that the receipt came from.
     * @property {string|number} purchasePrice - The price shown on the receipt for the product.
     * @property {Date} purchaseDate - The date that the product was purchased.
     * @property {string} receiptID - A unique ID that is shown on the receipt.
     */
})(angular);
